/**
 * C05 pacing overlays — shell-web load-time layer over frozen content-schema levels.
 *
 * Frozen paths (c0 pin honesty): packages/content-schema/levels/*.json, packages/sim.
 * All 45–90s duration tuning + beat placement lives here: scale path geometry,
 * wave pressure, optional pedestal/climax injection so every L1–5 run can hit
 * Agency → Growth → Combat → Chorus → Climax → Fail.
 *
 * Sim FORWARD_SPEED is fixed (6 u/s). Duration ≈ pathLength / FORWARD_SPEED for
 * pure path wins; climax combat adds time after pedestal for mecha levels.
 */

import { FORWARD_SPEED, type EnemyWaveDef, type LevelDef } from "@afr/sim";

/** Single fantasy-contract beat with optional timing / placement note. */
export interface PacingBeat {
  readonly beat: string;
  readonly at?: number | string;
  readonly note?: string;
}

/**
 * Loadable overlay document (one JSON per level under c05-pacing/).
 * Verifier requires levelId, targetDurationSec ∈ [45,90], and all six beat names.
 */
export interface PacingOverlay {
  readonly levelId: string;
  readonly targetDurationSec: number;
  readonly beats: readonly (string | PacingBeat)[];
  /** Explicit path length; defaults to targetDurationSec * FORWARD_SPEED. */
  readonly pathLength?: number;
  readonly startN?: number;
  /** Multiplier on authored wave HP (combat pressure). */
  readonly waveHpMul?: number;
  /** When true (or base lacks pedestal), inject pedestal at pedestalAtFrac. */
  readonly injectPedestal?: boolean;
  /** When true (or base lacks climax), inject climax wave after pedestal. */
  readonly injectClimax?: boolean;
  /** Pedestal z as fraction of paced pathLength (default 0.56). */
  readonly pedestalAtFrac?: number;
  /** Climax wave z as fraction of paced pathLength (default 0.74). */
  readonly climaxAtFrac?: number;
  readonly climaxHp?: number;
  readonly climaxDps?: number;
  readonly climaxContactHalfLength?: number;
}

const DEFAULT_PEDESTAL_FRAC = 0.56;
const DEFAULT_CLIMAX_FRAC = 0.74;

/** Scale a z along the run by path stretch factor. */
function scaleZ(z: number, scale: number): number {
  return Math.round(z * scale * 1000) / 1000;
}

/**
 * Apply a C05 pacing overlay onto a frozen base LevelDef.
 * Returns a new LevelDef suitable for createRun / RunLoop (sim accepts structurally).
 */
export function applyPacingOverlay(
  base: LevelDef,
  overlay: PacingOverlay,
): LevelDef {
  if (overlay.levelId !== base.id) {
    throw new Error(
      `Pacing overlay levelId ${overlay.levelId} does not match base ${base.id}`,
    );
  }

  const targetPath =
    overlay.pathLength ?? overlay.targetDurationSec * FORWARD_SPEED;
  if (!(targetPath > 0) || !Number.isFinite(targetPath)) {
    throw new Error(`Invalid paced pathLength: ${targetPath}`);
  }

  const scale = targetPath / base.pathLength;
  const waveHpMul = overlay.waveHpMul ?? 1;

  const gates = (base.gates ?? []).map((g) => ({
    ...g,
    z: scaleZ(g.z, scale),
  }));

  const waves = (base.waves ?? []).map((w) => ({
    ...w,
    z: scaleZ(w.z, scale),
    hp: Math.max(1, Math.round(w.hp * waveHpMul)),
  }));

  const pedestalFrac = overlay.pedestalAtFrac ?? DEFAULT_PEDESTAL_FRAC;
  const climaxFrac = overlay.climaxAtFrac ?? DEFAULT_CLIMAX_FRAC;

  let pedestal = base.pedestal
    ? { ...base.pedestal, z: scaleZ(base.pedestal.z, scale) }
    : undefined;

  // Place pedestal: inject if missing (or forced), otherwise re-pin to frac
  // so Chorus lands at the declared late-run beat (not proportional early).
  const shouldHavePedestal =
    pedestal !== undefined ||
    overlay.injectPedestal === true ||
    overlay.injectPedestal !== false;

  if (shouldHavePedestal) {
    pedestal = {
      id: pedestal?.id ?? `${base.id}-c05-pedestal`,
      z: scaleZ(targetPath * pedestalFrac, 1),
    };
  }

  let climaxWave: EnemyWaveDef | undefined;
  if (base.climaxWave) {
    const half =
      overlay.climaxContactHalfLength ?? base.climaxWave.contactHalfLength;
    climaxWave = {
      id: base.climaxWave.id,
      z: scaleZ(base.climaxWave.z, scale),
      hp: Math.max(
        1,
        Math.round(overlay.climaxHp ?? base.climaxWave.hp * waveHpMul),
      ),
      dps: overlay.climaxDps ?? base.climaxWave.dps,
      ...(half !== undefined ? { contactHalfLength: half } : {}),
    };
  }

  // Always ensure a climax when we have a pedestal so Climax beat can land.
  const shouldHaveClimax =
    climaxWave !== undefined ||
    overlay.injectClimax === true ||
    (overlay.injectClimax !== false && pedestal !== undefined);

  if (shouldHaveClimax) {
    const z = scaleZ(targetPath * climaxFrac, 1);
    const pedZ = pedestal?.z ?? z * 0.85;
    const climaxZ = Math.max(z, pedZ + 12);
    const half =
      overlay.climaxContactHalfLength ??
      climaxWave?.contactHalfLength ??
      20;
    climaxWave = {
      id: climaxWave?.id ?? `${base.id}-c05-climax`,
      z: Math.min(climaxZ, targetPath - 1),
      hp: overlay.climaxHp ?? climaxWave?.hp ?? 1600,
      dps: overlay.climaxDps ?? climaxWave?.dps ?? 12,
      contactHalfLength: half,
    };
  }

  // Ensure pedestal / climax sit within path
  if (pedestal && pedestal.z > targetPath) {
    pedestal = { ...pedestal, z: targetPath * pedestalFrac };
  }
  if (climaxWave && climaxWave.z > targetPath) {
    climaxWave = { ...climaxWave, z: targetPath * climaxFrac };
  }

  return {
    id: base.id,
    ...(base.name !== undefined ? { name: base.name } : {}),
    startN: overlay.startN ?? base.startN,
    pathLength: targetPath,
    gates,
    waves,
    ...(pedestal !== undefined ? { pedestal } : {}),
    ...(climaxWave !== undefined ? { climaxWave } : {}),
  };
}

/** Pure-travel estimate (seconds) for a paced pathLength at frozen FORWARD_SPEED. */
export function estimateTravelSec(pathLength: number): number {
  return pathLength / FORWARD_SPEED;
}

/** Beat names required by the fantasy contract (AD-REFERENCE.md). */
export const FANTASY_BEATS = [
  "Agency",
  "Growth",
  "Combat",
  "Chorus",
  "Climax",
  "Fail",
] as const;

export type FantasyBeat = (typeof FANTASY_BEATS)[number];

/** Extract beat name strings from an overlay (string or {beat}). */
export function overlayBeatNames(overlay: PacingOverlay): string[] {
  return overlay.beats.map((b) => (typeof b === "string" ? b : b.beat));
}

/** True if overlay declares all six fantasy-contract beats. */
export function overlayHasAllBeats(overlay: PacingOverlay): boolean {
  const have = new Set(overlayBeatNames(overlay));
  return FANTASY_BEATS.every((b) => have.has(b));
}
